import React, { useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import { Grid, TextField } from "@mui/material";
import moment from "moment";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

const getStyle = (width) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "auto", sm: width },
  maxWidth: "calc(100% - 32px)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
});

function AccessGrantWithDate(props) {
  const {
    open,
    handleClose,
    title,
    onCancel,
    onConfirm,
    cancelButtonLabel,
    confirmButtonLabel,
    width,
    effectiveFromDateLabel,
    effectiveToDateLabel,
  } = props;

  const [effectiveFromDate, setEffectiveFromDate] = useState(
    moment(new Date()).format("YYYY-MM-DD"),
  );
  const [effectiveToDate, setEffectiveToDate] = useState("");

  const today = moment(new Date()).startOf("day");
  const validFromDate =
    !!effectiveFromDate &&
    moment(effectiveFromDate, "YYYY-MM-DD", true).isValid() &&
    !moment(effectiveFromDate, "YYYY-MM-DD").isBefore(today, "day");
  const validToDate =
    !effectiveToDate ||
    (moment(effectiveToDate, "YYYY-MM-DD", true).isValid() &&
      !moment(effectiveToDate, "YYYY-MM-DD").isBefore(
        moment(effectiveFromDate, "YYYY-MM-DD"),
        "day",
      ));

  const resetDates = () => {
    setEffectiveFromDate(moment(new Date()).format("YYYY-MM-DD"));
    setEffectiveToDate("");
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={getStyle(width)}>
        <Typography id="modal-modal-title" sx={{ mb: 2 }}>
          {title}
        </Typography>

        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12} sm={6}>
              <div className="desktop-date-picker">
                <label htmlFor="effective-from-date">{effectiveFromDateLabel}</label>
                <span className="asterisk">*</span>
                <DesktopDatePicker
                  disablePast
                  className="form-label form-control date-field"
                  inputFormat="MM/DD/YYYY"
                  data-testid="effective-from-date"
                  minDate={moment(new Date()).format("YYYY-MM-DD")}
                  max="9999-12-31"
                  type="date"
                  value={effectiveFromDate}
                  onChange={(value) => {
                    setEffectiveFromDate(
                      value?._d ? moment(value._d).format("YYYY-MM-DD") : "",
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      id="effective-from-date"
                      {...params}
                      fullWidth
                      sx={{ mt: 1 }}
                    />
                  )}
                />
                {!validFromDate && (
                  <Typography color="error" variant="caption">
                    Effective From Date is required and cannot be in the past.
                  </Typography>
                )}
              </div>
            </Grid>

            <Grid item xs={12} sm={6}>
              <div className="desktop-date-picker">
                <label htmlFor="effective-to-date">{effectiveToDateLabel}</label>
                <DesktopDatePicker
                  disablePast
                  className="form-label form-control date-field"
                  inputFormat="MM/DD/YYYY"
                  data-testid="effective-to-date"
                  minDate={moment(effectiveFromDate || new Date()).format("YYYY-MM-DD")}
                  max="9999-12-31"
                  type="date"
                  value={effectiveToDate}
                  onChange={(value) => {
                    setEffectiveToDate(
                      value?._d ? moment(value._d).format("YYYY-MM-DD") : "",
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      id="effective-to-date"
                      {...params}
                      fullWidth
                      sx={{ mt: 1 }}
                    />
                  )}
                />
                {!validToDate && (
                  <Typography color="error" variant="caption">
                    Effective To Date cannot be earlier than Effective From Date.
                  </Typography>
                )}
              </div>
            </Grid>
          </Grid>
        </LocalizationProvider>

        <div className="action-btn-div mt-3">
          <Button
            className="action-button"
            variant="outlined"
            onClick={(e) => {
              resetDates();
              onCancel(e);
            }}
            data-testid="cancel-action"
          >
            {cancelButtonLabel}
          </Button>
          <Button
            className="action-button ml-4"
            variant="contained"
            onClick={() => {
              onConfirm({
                effectiveFromDate: effectiveFromDate || null,
                effectiveToDate: effectiveToDate || null,
              });
              resetDates();
            }}
            data-testid="confirm-action"
            disabled={!validFromDate || !validToDate}
          >
            {confirmButtonLabel}
          </Button>
        </div>
      </Box>
    </Modal>
  );
}

AccessGrantWithDate.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  cancelButtonLabel: PropTypes.string,
  confirmButtonLabel: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  effectiveFromDateLabel: PropTypes.string,
  effectiveToDateLabel: PropTypes.string,
};

AccessGrantWithDate.defaultProps = {
  cancelButtonLabel: "No",
  confirmButtonLabel: "Yes",
  width: 640,
  effectiveFromDateLabel: "Effective From Date",
  effectiveToDateLabel: "Effective To Date",
};

export default AccessGrantWithDate;
